import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { ICiscoPolicy } from '../../../services/hwCisco/ciscoPolicy/ciscoPolicy.model';
import { RootState } from '../../app.model';
import {
  deleteCiscoPolicy,
  getCiscoPolicyById,
  saveCiscoPolicy,
  searchCiscoPolicy,
} from './ciscoPolicy.action';
import { ICiscoPolicyState } from './ciscoPolicy.model';

export const initialState: ICiscoPolicyState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const ciscoPolicySlice = createSlice({
  name: 'ciscoPolicy',
  initialState,
  reducers: {
    clearCiscoPolicy: () => {
      return initialState;
    },
    clearCiscoPolicyMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearCiscoPolicyGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchCiscoPolicy.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchCiscoPolicy.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<ICiscoPolicy>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchCiscoPolicy.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getCiscoPolicyById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getCiscoPolicyById.fulfilled.type]: (state, action: PayloadAction<ICiscoPolicy>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getCiscoPolicyById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveCiscoPolicy.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveCiscoPolicy.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveCiscoPolicy.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteCiscoPolicy.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteCiscoPolicy.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteCiscoPolicy.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const ciscoPolicySelector = (state: RootState) => state.ciscoPolicy;

// Actions
export const {
  clearCiscoPolicy,
  clearCiscoPolicyMessages,
  clearCiscoPolicyGetById,
  setTableColumnSelection,
} = ciscoPolicySlice.actions;

// The reducer
export default ciscoPolicySlice.reducer;
